import { useDataProvider } from "src/system/useDataProvider.ts";
import { useQuery, type UseQueryOptions } from "@tanstack/react-query";
import { useResourceQueries } from "src/framework/useResourceQueries.ts";
import { useContentLocale } from "src/auth/hooks/useContentLocale.tsx";

export type ValidUseOneData<Resource = any> = {
  data: Resource;
  available_languages?: string[];
};

export type UseOneQueryOptions<
  Resource = any,
  Data = ValidUseOneData<Resource>,
> = Omit<UseQueryOptions<Data, any, Data>, "queryFn" | "queryKey">;

export const useOne = <
  Resource = any,
  Data = ValidUseOneData<Resource>,
>(props: {
  resource: string;
  id: string;
  lang?: string;
  meta?: any;
  requestParams?: Record<string, any>;
  includes?: string[];
  queryOptions?: UseOneQueryOptions<Resource, Data>;
}) => {
  const { resource, id, lang, queryOptions, requestParams, includes } = props;
  const dataProvider = useDataProvider();

  const queries = useResourceQueries();

  const defaultLang = useContentLocale();
  const locale = lang || defaultLang;
  return useQuery({
    queryKey: queries.detail({
      id,
      locale: locale,
      include: includes,
      resource,
      requestParams,
    }),
    queryFn: ({ signal }) => {
      return dataProvider.getOne({
        id,
        signal,
        resource,
        include: includes,
        locale: locale,
      });
    },
    ...queryOptions,
  });
};
